import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import moment from 'moment';

import TokenService from 'app/domain/authentication/tokenService';
import store from 'store/configureStore';
import { convertDateToUnix } from 'utils/date';
import { LS_KEYS, setItem } from 'utils/localStorage';

import { ERROR_CODES, LOCAL_BASE_URL } from './constants';
import { HTTPVerbs } from './types';

const httpService: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASEURL || LOCAL_BASE_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

interface RetryConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

export interface CommonErrorResponse {
  code: string;
  error_type: string;
}

const onResponseError = async (error: AxiosError<CommonErrorResponse>) => {
  const isOnboardingServiceEnabled =
    process.env.REACT_APP_ONBOARDING_SERVICE_ENABLED === 'true' &&
    !!store.getState().authentication.group_code &&
    !store.getState().authentication?.isPostSubmissionLogin;
  const originalConfig = error.config as RetryConfig;

  if (error?.response?.status === ERROR_CODES.UNAUTHORIZED_ERROR) {
    setItem(LS_KEYS.showNetworkErrorModal, 'false');

    const loginAPIs = [
      '/auth-service/v1/otp/resend',
      '/auth-service/v1/login',
      '/auth-service/v1/otp/login/validate',
      '/onboarding/v3/otp/generate/customer',
      '/onboarding/v3/otp/generate/customer?generate_type=RESEND',
      '/onboarding/v3/otp/authenticate/customer',
    ];
    const isLoginRequest = loginAPIs.some(val =>
      originalConfig.url?.includes(val),
    );

    if (isLoginRequest) {
      window.location.replace('/');
    }

    if (!originalConfig._retry) {
      originalConfig._retry = true;
      const refreshToken = TokenService.getRefreshTokenLS();
      const refreshTokenTTL = TokenService.getRefreshTokenTTL();
      const currentDT = convertDateToUnix(moment());

      // check if refresh token is expired
      if (refreshTokenTTL < currentDT) {
        originalConfig._retry = false;
        TokenService.removeUserAuthLS();
        window.location.replace('/');
      }

      try {
        const extendAuthTokenUrl = `/auth-service/v1/extend_access_token`;
        const extendOnboardingTokenUrl = `/onboarding/v3/jwt/extend_access_token`;
        const result = await httpService({
          headers: {
            ...originalConfig.headers,
            'Content-Type': 'application/json',
            'x-auth-app-id': process.env.REACT_APP_X_AUTH_APP_ID ?? '',
          },
          method: HTTPVerbs.POST,
          data: { refresh_token: refreshToken },
          url: isOnboardingServiceEnabled
            ? extendOnboardingTokenUrl
            : extendAuthTokenUrl,
        });

        TokenService.setUserAuthLS(result.data.data);
        const config = {
          ...originalConfig,
          data: {
            ...JSON.parse(originalConfig.data),
          },
        };

        return httpService({
          ...config,
          headers: {
            ...originalConfig.headers,
            Authorization: `Bearer ${result.data.data.access_token}`,
          },
        });
      } catch (err) {
        return Promise.reject(err);
      }
    } else {
      originalConfig._retry = false;
    }
  }

  return Promise.reject(error);
};

httpService.interceptors.request.use(
  request => request,
  (
    error: AxiosError<CommonErrorResponse>,
  ): Promise<AxiosError<CommonErrorResponse>> => Promise.reject(error),
);
httpService.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  (error: AxiosError<CommonErrorResponse>) => onResponseError(error),
);

export default httpService;
